import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Image as ImageIcon,
  Edit2,
  Save,
  X,
  Shield,
  Award,
  Users,
  LocateFixed,
  Landmark,
  Hash,
  Home,
  AlertCircle,
  Navigation,
  Globe,
  Smartphone,
  Send,
  Key,
  AlertTriangle
} from "lucide-react";
import {
  adminProfile,
  adminSendOtp,
  adminVerifyOtp,
  requestAdminProfileEdit,
  editAdminProfile
} from "../Apis/AdminApis";
import PopupMessage from "../PopupMessage/PopupMessage";
import "./AdminProfile.css";
import Sidebar from "../Sidebar/Sidebar";
import pavamanLogo from "../images/drone.png"; 
import { PiDroneBold } from "react-icons/pi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const AdminProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("info");
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [showGSTPreview, setShowGSTPreview] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
  const [showMobileVerifyPopup, setShowMobileVerifyPopup] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
const [isEditRequestProcessing, setIsEditRequestProcessing] = useState(false);
const [showWithdrawRequestModal, setShowWithdrawRequestModal] = useState(false);
const [isWithdrawProcessing, setIsWithdrawProcessing] = useState(false);
const [isSaving, setIsSaving] = useState(false);
const [isLookingUpPincode, setIsLookingUpPincode] = useState(false);
const [pincodeLocationValid, setPincodeLocationValid] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const response = await adminProfile();
 if (response?.error) {

      // Session expired
      if (response?.error?.includes("Unauthorized")) {
        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        return;
      }

      // Other API errors
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);

      return;
    }
      
        setProfile(response);
        setEditFormData(response);
      
    } catch (error) {
      console.error("Error loading profile:", error);
      if (
      error?.response?.status === 401 ||
      error?.response?.data?.error?.includes("Unauthorized")
    ) {
      setPopupMessage("Session expired, redirecting to login page");
      setPopupType("error");
      setPopupOpen(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);

      return;
    }

      setPopupMessage("Failed to load profile data");
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

const getApprovalStatus = (status) => {
  switch(status) {
    case 0:
      return { 
        text: "Invitation Sent", 
        icon: <Clock size={18} />, 
        color: "#f59e0b", 
        bg: "#fed7aa" 
      };

    case 1:
      return { 
        text: "Registration Completed", 
        icon: <CheckCircle size={18} />, 
        color: "#3b82f6", 
        bg: "#dbeafe" 
      };

    case 2:
      return { 
        text: "Approved", 
        icon: <CheckCircle size={18} />, 
        color: "#10b981", 
        bg: "#d1fae5" 
      };

    default:
      return { 
        text: "Unknown", 
        icon: <AlertCircle size={18} />, 
        color: "#6b7280", 
        bg: "#f3f4f6" 
      };
  }
};

  const getMobileVerificationStatus = () => {
    if (profile?.mobile_verified) {
      return { text: "Verified", icon: <CheckCircle size={16} />, color: "#10b981", bg: "#d1fae5" };
    }
    return { text: "Not Verified", icon: <XCircle size={16} />, color: "#dc2626", bg: "#fee2e2" };
  };

const handleEditClick = () => {
  // Already approved
  if (profile.profile_edit_status === 2) {
    setEditFormData(profile);
    setIsEditing(true);
    return;
  }

  // Already pending
  if (profile.profile_edit_status === 1) {
    setPopupMessage(
      "Profile edit request is already pending with the superadmin."
    );
    setPopupType("info");
    setPopupOpen(true);
    return;
  }

  // Show confirmation popup
  setShowEditRequestModal(true);
};

const handleConfirmEditRequest = async () => {
  setIsEditRequestProcessing(true);

  try {
    const response = await requestAdminProfileEdit({
      action: "request",
    });

    if (response?.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setProfile((prev) => ({
      ...prev,
      profile_edit_status: response.profile_edit_status,
    }));

    setShowEditRequestModal(false);

    setPopupMessage(
      "Profile edit request sent to superadmin successfully."
    );
    setPopupType("success");
    setPopupOpen(true);

  } catch (error) {
    console.error("Profile edit request error:", error);

    setPopupMessage(
      "Failed to send profile edit request."
    );
    setPopupType("error");
    setPopupOpen(true);
  } finally {
    setIsEditRequestProcessing(false);
  }
};

const handleWithdrawEditRequest = async () => {
  setIsWithdrawProcessing(true);

  try {
    const response = await requestAdminProfileEdit({
      action: "withdraw",
    });

    if (response?.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setProfile((prev) => ({
      ...prev,
      profile_edit_status: response.profile_edit_status,
    }));

    setShowWithdrawRequestModal(false);

    setPopupMessage(
      "Profile edit request withdrawn successfully."
    );
    setPopupType("success");
    setPopupOpen(true);

  } catch (error) {
    console.error("Withdraw profile edit request error:", error);

    setPopupMessage(
      "Failed to withdraw profile edit request."
    );
    setPopupType("error");
    setPopupOpen(true);
  } finally {
    setIsWithdrawProcessing(false);
  }
};

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData(profile);
  };
const fetchLocationFromPincode = async (pincode) => {
  if (!pincode || pincode.length !== 6) {
    setPincodeLocationValid(false);

    setEditFormData((prev) => ({
      ...prev,
      mandal: "",
      city: "",
      district: "",
      state: "",
    }));

    return;
  }

  if (!/^\d{6}$/.test(pincode)) {
    setPincodeLocationValid(false);
    return;
  }

  setIsLookingUpPincode(true);

  try {
    const response = await fetch(
      `https://api.postalpincode.in/pincode/${pincode}`
    );

    const data = await response.json();

    if (
      data &&
      data[0] &&
      data[0].Status === "Success" &&
      data[0].PostOffice &&
      data[0].PostOffice.length > 0
    ) {
      const po = data[0].PostOffice[0];

      setEditFormData((prev) => ({
        ...prev,
        mandal: po.Block || "N/A",
        district: po.District || "N/A",
        city: po.District || "N/A",
        state: po.State || "N/A",
      }));

      setPincodeLocationValid(true);

      setPopupMessage("Location details fetched successfully!");
      setPopupType("success");
      setPopupOpen(true);

      setTimeout(() => {
        setPopupOpen(false);
      }, 2000);
    } else {
      setEditFormData((prev) => ({
        ...prev,
        mandal: "",
        city: "",
        district: "",
        state: "",
      }));

      setPincodeLocationValid(false);

      setPopupMessage("Invalid pincode");
      setPopupType("error");
      setPopupOpen(true);
    }
  } catch (error) {
    console.error("Error fetching pincode details:", error);

    setEditFormData((prev) => ({
      ...prev,
      mandal: "",
      city: "",
      district: "",
      state: "",
    }));

    setPincodeLocationValid(false);

    setPopupMessage("Error fetching location details");
    setPopupType("error");
    setPopupOpen(true);
  } finally {
    setIsLookingUpPincode(false);
  }
};

const handleInputChange = (e) => {
  const { name, value } = e.target;

  setEditFormData((prev) => ({
    ...prev,
    [name]: value,
  }));

  // Pincode handling
  if (name === "pincode") {
    // Clear location if pincode is incomplete
    if (value.length !== 6) {
      setPincodeLocationValid(false);

      setEditFormData((prev) => ({
        ...prev,
        pincode: value,
        mandal: "",
        city: "",
        district: "",
        state: "",
      }));

      return;
    }

    // Fetch location when exactly 6 digits are entered
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      fetchLocationFromPincode(value);
    }
  }
};

const handleSaveChanges = async () => {
  // Validate pincode
  if (
    !editFormData.pincode ||
    !/^\d{6}$/.test(editFormData.pincode)
  ) {
    setPopupMessage("Please enter a valid 6-digit pincode.");
    setPopupType("error");
    setPopupOpen(true);
    return;
  }

  // Don't allow save while location is being fetched
  if (isLookingUpPincode) {
    setPopupMessage("Please wait while location details are being fetched.");
    setPopupType("info");
    setPopupOpen(true);
    return;
  }

  // Don't allow save if pincode did not return location
  if (
    !pincodeLocationValid ||
    !editFormData.state ||
    editFormData.state === "N/A"
  ) {
    setPopupMessage(
      "Please enter a valid pincode and wait for the location details to be fetched."
    );
    setPopupType("error");
    setPopupOpen(true);
    return;
  }

  setIsSaving(true);

  try {
    const payload = {
      name: editFormData.name,
      email: editFormData.email,
      contact_name: editFormData.contact_name,
      mobile_no: editFormData.mobile_no,
      country_code: editFormData.country_code || "91",
      pincode: editFormData.pincode,
      landmark: editFormData.landmark,
      address: editFormData.address,

      // Location details
      mandal: editFormData.mandal,
      city: editFormData.city,
      district: editFormData.district,
      state: editFormData.state,
    };

    const response = await editAdminProfile(payload);

    if (response?.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setProfile(response.data);
    setEditFormData(response.data);

    setIsEditing(false);

    setPincodeLocationValid(true);

    if (response.data?.name) {
      localStorage.setItem("company_name", response.data.name);
    }

    setPopupMessage(
      response.message || "Admin profile updated successfully."
    );
    setPopupType("success");
    setPopupOpen(true);

  } catch (error) {
    console.error("Edit admin profile error:", error);

    setPopupMessage(
      error?.response?.data?.error ||
      "Failed to update profile. Please try again."
    );

    setPopupType("error");
    setPopupOpen(true);

  } finally {
    setIsSaving(false);
  }
};

  const handleSendOTP = async () => {
    setOtpSending(true);
    try {
      const response = await adminSendOtp({ action: "send_otp" });
      
      if (response.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
      } else {
        setOtpSent(true);
        setCountdown(120); 
        setPopupMessage("OTP sent successfully to your registered mobile number!");
        setPopupType("success");
        setPopupOpen(true);
      }
    } catch (error) {
      setPopupMessage("Failed to send OTP. Please try again.");
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setPopupMessage("Please enter a valid 6-digit OTP");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setOtpVerifying(true);
    try {
      const response = await adminVerifyOtp({ 
        action: "mobile_verify",
        otp: otpCode 
      });
      
      if (response.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
      } else {
        setPopupMessage("Mobile number verified successfully!");
        setPopupType("success");
        setPopupOpen(true);
        setProfile(prev => ({
          ...prev,
          mobile_verified: true
        }));
        setShowMobileVerifyPopup(false);
        setOtpCode("");
        setOtpSent(false);
        setCountdown(0);
      }
    } catch (error) {
      setPopupMessage("Failed to verify OTP. Please try again.");
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResendOTP = () => {
    if (countdown === 0) {
      handleSendOTP();
    }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="admin-profile-stat-card">
      <div className="admin-profile-stat-icon" style={{ backgroundColor: color + '20', color: color }}>
        <Icon size={24} />
      </div>
      <div className="admin-profile-stat-info">
        <span className="admin-profile-stat-value">{value}</span>
        <span className="admin-profile-stat-label">{label}</span>
      </div>
    </div>
  );

  const InfoRow = ({ icon: Icon, label, value, isLink = false }) => (
    <div className="admin-profile-info-row">
      <div className="admin-profile-info-icon">
        <Icon size={18} />
      </div>
      <div className="admin-profile-info-content">
        <span className="admin-profile-info-label">{label}</span>
        {isLink && value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="admin-profile-info-value-link">
            {value}
          </a>
        ) : (
          <span className="admin-profile-info-value">{value || "Not provided"}</span>
        )}
      </div>
    </div>
  );

   if (isLoading) {
  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="dashboard-content">
        <div className="custom-loading-container">
          <img src={pavamanLogo} alt="Loading" className="spinning-logo" />
          <p className="loading-text">
          Loading admin profile...
          </p>
        </div>
      </div>
    </div>
  );
}


if (!profile) {
  return (
    <>
      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />
       <div className="dashboard-wrapper">
        <Sidebar />
         <div className="dashboard-content"></div>
        </div>
    </>
   
  );
}
  const approvalStatus = getApprovalStatus(profile.approval);
  const mobileStatus = getMobileVerificationStatus();

  return (
    <>
      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />

      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="dashboard-content">
          <div className="admin-profile-header">
            <div className="admin-profile-header-content">
              <div className="admin-profile-avatar">
                {profile.logo_url ? (
                  <img src={profile.logo_url} alt={profile.name} />
                ) : (
                  <div className="admin-profile-avatar-placeholder">
                    <Building2 size={40} />
                  </div>
                )}
              </div>
              <div className="admin-profile-header-info">
                <h1 className="admin-profile-title">{profile.name}</h1>
                <p className="admin-profile-subtitle">{profile.email}</p>
                <div className="admin-profile-status-badge" style={{ backgroundColor: approvalStatus.bg, color: approvalStatus.color }}>
                  {approvalStatus.icon}
                  <span>{approvalStatus.text}</span>
                </div>
              </div>
              {!isEditing && (
  <button
    className="admin-profile-edit-btn"
    onClick={handleEditClick}
    disabled={profile.profile_edit_status === 1}
  >
    {profile.profile_edit_status === 1 ? (
      <>
        <Clock size={18} />
        Edit Request Pending
      </>
    ) : profile.profile_edit_status === 2 ? (
      <>
        <Edit2 size={18} />
        Edit Profile
      </>
    ) : (
      <>
        <Send size={18} />
        Edit Profile Request
      </>
    )}
  </button>
  
)}
{!isEditing && profile.profile_edit_status === 1 && (
  <button
    className="admin-profile-cancel-btn"
    onClick={() => setShowWithdrawRequestModal(true)}
  >
    <X size={18} />
    Withdraw Request
  </button>
)}
            </div>
          </div>

         {!profile.mobile_verified && !profile.is_superadmin_company && (
  <div className="admin-profile-verification-alert">
              <AlertTriangle size={20} />
              <div className="alert-content">
                <strong>Mobile Number Not Verified!</strong>
                <p>Please verify your mobile number to approve or reject pilots.</p>
              </div>
              <button 
                className="verify-mobile-btn"
                onClick={() => {
  if (!profile.is_superadmin_company) {
    setShowMobileVerifyPopup(true);
  }
}}
              >
                <Smartphone size={18} />
                Verify Now
              </button>
            </div>
          )}

          <div className="admin-profile-main">
            <div className="admin-profile-section">
              <div className="admin-profile-section-header">
                <Building2 size={20} />
                <h3>Company Information</h3>
              </div>
              <div className="admin-profile-section-content">
                {isEditing ? (
                  <>
                    <div className="admin-profile-edit-field">
                      <label>Company Name</label>
                      <input
                        type="text"
                        name="name"
                        value={editFormData.name || ""}
                        onChange={handleInputChange}
                        className="admin-profile-input"
                      />
                    </div>
                    <div className="admin-profile-edit-field">
                      <label>Contact Person</label>
                      <input
                        type="text"
                        name="contact_name"
                        value={editFormData.contact_name || ""}
                        onChange={handleInputChange}
                        className="admin-profile-input"
                      />
                    </div>
                    <div className="admin-profile-edit-field">
  <label>Email</label>
  <input
    type="email"
    name="email"
    value={editFormData.email || ""}
    onChange={handleInputChange}
    className="admin-profile-input"
    placeholder="Enter email address"
  />
</div>
                  <div className="admin-profile-edit-field">
  <label>Mobile Number</label>

  <div className="mobile-field-with-status">
    <PhoneInput
      country={"in"}
      value={editFormData.mobile_no || ""}
      onChange={(phone, countryData) => {
        setEditFormData((prev) => ({
          ...prev,
          mobile_no: phone,
          country_code: countryData.dialCode,
        }));
      }}
      inputClass="admin-profile-input"
      containerClass="admin-profile-phone-input-container"
      enableSearch={true}
      placeholder="Enter mobile number"
      disabled={isSaving}
    />

    <span
      className={`mobile-status-badge ${
        editFormData.mobile_no !== profile.mobile_no
          ? "unverified"
          : profile.mobile_verified
            ? "verified"
            : "unverified"
      }`}
    >
      {editFormData.mobile_no !== profile.mobile_no ? (
        <>
          <XCircle size={14} />
          Verification Required
        </>
      ) : profile.mobile_verified ? (
        <>
          <CheckCircle size={14} />
          Verified
        </>
      ) : (
        <>
          <XCircle size={14} />
          Not Verified
        </>
      )}
    </span>
  </div>
</div>
                  </>
                ) : (
                 <>
  <InfoRow icon={Building2} label="Company Name" value={profile.name} />
  <InfoRow icon={User} label="Contact Person" value={profile.contact_name} />
  <InfoRow icon={Mail} label="Email Address" value={profile.email} />
  <InfoRow icon={Phone} label="Mobile Number" value={profile.mobile_no} />

  {!profile.is_superadmin_company && (
    <div className="admin-profile-info-row">
      <div className="admin-profile-info-icon">
        <Shield size={18} />
      </div>
      <div className="admin-profile-info-content">
        <span className="admin-profile-info-label">Mobile Verification</span>
        <span className={`verification-status ${profile.mobile_verified ? 'verified' : 'unverified'}`}>
          {mobileStatus.icon}
          {mobileStatus.text}
        </span>
      </div>
    </div>
  )}
</>
                )}
              </div>
            </div>

            <div className="admin-profile-section">
              <div className="admin-profile-section-header">
                <MapPin size={20} />
                <h3>Address Details</h3>
              </div>
              <div className="admin-profile-section-content">
                {isEditing ? (
                  <>
                    <div className="admin-profile-edit-field">
                      <label>Address</label>
                      <textarea
                        name="address"
                        value={editFormData.address || ""}
                        onChange={handleInputChange}
                        className="admin-profile-textarea"
                        rows="3"
                      />
                    </div>
                    <div className="admin-profile-edit-row">
                      <div className="admin-profile-edit-field">
                        <label>Landmark</label>
                        <input
                          type="text"
                          name="landmark"
                          value={editFormData.landmark || ""}
                          onChange={handleInputChange}
                          className="admin-profile-input"
                        />
                      </div>
<div className="admin-profile-edit-field">
  <label>
    Pincode
    <span className="admin-profile-required">*</span>
  </label>

  <input
    type="text"
    name="pincode"
    value={editFormData.pincode || ""}
    onChange={(e) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 6);

      setEditFormData((prev) => ({
        ...prev,
        pincode: value,
      }));

      if (value.length !== 6) {
        setPincodeLocationValid(false);

        setEditFormData((prev) => ({
          ...prev,
          mandal: "",
          city: "",
          district: "",
          state: "",
        }));
      } else {
        fetchLocationFromPincode(value);
      }
    }}
    className="admin-profile-input"
    maxLength="6"
    placeholder="Enter 6-digit pincode"
    disabled={isLookingUpPincode || isSaving}
  />

  {isLookingUpPincode && (
    <div className="admin-profile-pincode-loading">
      Fetching location...
    </div>
  )}

  {!isLookingUpPincode &&
    editFormData.pincode?.length === 6 &&
    pincodeLocationValid && (
      <div className="admin-profile-pincode-success">
        ✓ Location details fetched successfully
      </div>
    )}
</div>
                    </div>
                   <div className="admin-profile-edit-row">
  <div className="admin-profile-edit-field">
    <label>City</label>
    <input
      type="text"
      value={editFormData.city || ""}
      disabled
      className="admin-profile-input admin-profile-input-disabled"
    />
  </div>

  <div className="admin-profile-edit-field">
    <label>District</label>
    <input
      type="text"
      value={editFormData.district || ""}
      disabled
      className="admin-profile-input admin-profile-input-disabled"
    />
  </div>
</div>
                   <div className="admin-profile-edit-row">
  <div className="admin-profile-edit-field">
    <label>State</label>
    <input
      type="text"
      value={editFormData.state || ""}
      disabled
      className="admin-profile-input admin-profile-input-disabled"
    />
  </div>

  <div className="admin-profile-edit-field">
    <label>Mandal</label>
    <input
      type="text"
      value={editFormData.mandal || ""}
      disabled
      className="admin-profile-input admin-profile-input-disabled"
    />
  </div>
</div>
                  </>
                ) : (
                  <>
                    <InfoRow icon={Home} label="Address" value={profile.address} />
                    <InfoRow icon={Landmark} label="Landmark" value={profile.landmark} />
                    <InfoRow icon={Hash} label="Pincode" value={profile.pincode} />
                    <InfoRow icon={Navigation} label="City" value={profile.city} />
                    <InfoRow icon={LocateFixed} label="District" value={profile.district} />
                    <InfoRow icon={Globe} label="State" value={profile.state} />
                  </>
                )}
              </div>
            </div>

            <div className="admin-profile-section">
              <div className="admin-profile-section-header">
                <FileText size={20} />
                <h3>Documents</h3>
              </div>
              <div className="admin-profile-documents">
                <div className="admin-profile-document-card" onClick={() => setShowLogoPreview(true)}>
                  <div className="admin-profile-document-icon">
                    <ImageIcon size={32} />
                  </div>
                  <div className="admin-profile-document-info">
                    <h4>Company Logo</h4>
                    <p>{profile.logo_url ? "Click to view" : "Not uploaded"}</p>
                  </div>
                </div>
                <div className="admin-profile-document-card" onClick={() => setShowGSTPreview(true)}>
                  <div className="admin-profile-document-icon">
                    <FileText size={32} />
                  </div>
                  <div className="admin-profile-document-info">
                    <h4>GST Certificate</h4>
                    <p>{profile.gst_url ? "Click to view" : "Not uploaded"}</p>
                  </div>
                </div>
              </div>
            </div>

          {!profile.mobile_verified && !profile.is_superadmin_company && (
  <div className="admin-profile-warning-card">
                <AlertCircle size={20} />
                <div>
                  <strong>Access Restricted</strong>
                  <p>You need to verify your mobile number before you can approve or reject pilots.</p>
                </div>
              </div>
            )}
          </div>

          {isEditing && (
            <div className="admin-profile-edit-actions">
              <button className="admin-profile-cancel-btn" onClick={handleCancelEdit}>
                <X size={18} />
                Cancel
              </button>
              <button
  className="admin-profile-save-btn"
  onClick={handleSaveChanges}
  disabled={
    isSaving ||
    isLookingUpPincode ||
    !editFormData.pincode ||
    editFormData.pincode.length !== 6 ||
    !pincodeLocationValid ||
    !editFormData.state
  }
>
 {isSaving ? (
  <>
    <span className="admin-profile-spinner"></span>
    Saving...
  </>
) : isLookingUpPincode ? (
  <>
    Fetching Location...
  </>
) : (
  <>
    <Save size={18} />
    Save Changes
  </>
)}
</button>
            </div>
          )}
        </div>
      </div>

      {showEditRequestModal && (
  <div
    className="tr-modal-overlay"
    onClick={() => {
      if (!isEditRequestProcessing) {
        setShowEditRequestModal(false);
      }
    }}
  >
    <div
      className="admin-profile-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="tr-modal-header">
        <h3>
          {/* <Edit2 size={20} /> */}
          Request Profile Edit
        </h3>

        <button
          className="tr-modal-close"
          onClick={() => {
            if (!isEditRequestProcessing) {
              setShowEditRequestModal(false);
            }
          }}
          disabled={isEditRequestProcessing}
        >
          <X size={20} />
        </button>
      </div>

      <div className="tr-modal-body">
        <div className="tr-confirm-container">

          <div className="tr-confirm-icon tr-confirm-approve">
            ✏️
          </div>

          <p className="tr-confirm-text">
            Are you sure you want to request permission to edit your profile?
          </p>

          <p className="tr-confirm-subtext">
            Your request will be sent to the superadmin for approval.
            {/* You will be able to edit your profile after the request is approved. */}
          </p>

          <div className="tr-confirm-buttons">

            <button
              className="tr-confirm-cancel"
              onClick={() => setShowEditRequestModal(false)}
              disabled={isEditRequestProcessing}
            >
              Cancel
            </button>

            <button
              className="tr-confirm-approve-btn"
              onClick={handleConfirmEditRequest}
              disabled={isEditRequestProcessing}
            >
              {isEditRequestProcessing ? (
                <>
                  <span className="tr-spinner-small"></span>
                  Sending...
                </>
              ) : (
                <>
                  {/* <Send size={16} /> */}
                  Yes, Request Edit
                </>
              )}
            </button>

          </div>
        </div>
      </div>
    </div>
  </div>
)}

{showWithdrawRequestModal && (
  <div
    className="tr-modal-overlay"
    onClick={() => {
      if (!isWithdrawProcessing) {
        setShowWithdrawRequestModal(false);
      }
    }}
  >
    <div
      className="admin-profile-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="tr-modal-header">
        <h3>
          {/* <X size={20} /> */}
          Withdraw Profile Edit Request
        </h3>

        <button
          className="tr-modal-close"
          onClick={() => {
            if (!isWithdrawProcessing) {
              setShowWithdrawRequestModal(false);
            }
          }}
          disabled={isWithdrawProcessing}
        >
          <X size={20} />
        </button>
      </div>

      <div className="tr-modal-body">
        <div className="tr-confirm-container">

          <div className="tr-confirm-icon tr-confirm-reject">
            ↩️
          </div>

          <p className="tr-confirm-text">
            Are you sure you want to withdraw your profile edit request?
          </p>

          <p className="tr-confirm-subtext">
            Your pending profile edit request will be cancelled.
            {/* the superadmin will no longer be able to approve this request. */}
          </p>

          <div className="tr-confirm-buttons">

            <button
              className="tr-confirm-cancel"
              onClick={() => setShowWithdrawRequestModal(false)}
              disabled={isWithdrawProcessing}
            >
              Cancel
            </button>

            <button
              className="tr-confirm-reject-btn"
              onClick={handleWithdrawEditRequest}
              disabled={isWithdrawProcessing}
            >
              {isWithdrawProcessing ? (
                <>
                  <span className="tr-spinner-small"></span>
                  Withdrawing...
                </>
              ) : (
                <>
                  {/* <X size={16} /> */}
                  Yes, Withdraw
                </>
              )}
            </button>

          </div>
        </div>
      </div>
    </div>
  </div>
)}

      {showMobileVerifyPopup && !profile.is_superadmin_company && (
        <div className="admin-profile-modal-overlay" onClick={() => {
          setShowMobileVerifyPopup(false);
          setOtpCode("");
          setOtpSent(false);
          setCountdown(0);
        }}>
          <div className="admin-profile-modal mobile-verify-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-profile-modal-header">
              <h3>
                <Smartphone size={20} />
                Verify Mobile Number
              </h3>
              <button className="admin-profile-modal-close" onClick={() => {
                setShowMobileVerifyPopup(false);
                setOtpCode("");
                setOtpSent(false);
                setCountdown(0);
              }}>
                <X size={20} />
              </button>
            </div>
            <div className="admin-profile-modal-body">
              <div className="mobile-verify-info">
                <Phone size={40} />
                <p>We'll send a verification code to your registered mobile number:</p>
                <strong>{profile.mobile_no}</strong>
              </div>
              
              {!otpSent ? (
                <button 
                  className="send-otp-btn"
                  onClick={handleSendOTP}
                  disabled={otpSending}
                >
                  {otpSending ? (
                    <>
                      <div className="spinner-small"></div>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Verification Code
                    </>
                  )}
                </button>
              ) : (
                <div className="otp-verification-section">
                  <div className="otp-input-group">
                    <label>Enter OTP</label>
                    <div className="otp-input-wrapper">
                      <Key size={18} />
                      <input
                        type="text"
                        maxLength="6"
                        placeholder="Enter 6-digit OTP"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                        className="otp-input"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="ap-otp-actions">
                    <button 
                      className="verify-otp-btn"
                      onClick={handleVerifyOTP}
                      disabled={otpVerifying || !otpCode}
                    >
                      {otpVerifying ? (
                        <>
                          <div className="spinner-small"></div>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={18} />
                          Verify OTP
                        </>
                      )}
                    </button>
                    <button 
                      className={`resend-otp-btn ${countdown > 0 ? 'disabled' : ''}`}
                      onClick={handleResendOTP}
                      disabled={countdown > 0 || otpVerifying}
                    >
                      {countdown > 0 ? `Resend in ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}` : 'Resend OTP'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showLogoPreview && profile.logo_url && (
        <div className="admin-profile-modal-overlay" onClick={() => setShowLogoPreview(false)}>
          <div className="admin-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-profile-modal-header">
              <h3>Company Logo</h3>
              <button className="admin-profile-modal-close" onClick={() => setShowLogoPreview(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="admin-profile-modal-body">
              <img src={profile.logo_url} alt="Company Logo" />
            </div>
          </div>
        </div>
      )}

      {showGSTPreview && profile.gst_url && (
        <div className="admin-profile-modal-overlay" onClick={() => setShowGSTPreview(false)}>
          <div className="admin-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-profile-modal-header">
              <h3>GST Certificate</h3>
              <button className="admin-profile-modal-close" onClick={() => setShowGSTPreview(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="admin-profile-modal-body">
              {profile.gst_url.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                <img src={profile.gst_url} alt="GST Certificate" />
              ) : (
                <iframe src={profile.gst_url} title="GST Certificate" />
              )}
            </div>
            <div className="admin-profile-modal-footer">
              <a href={profile.gst_url} download className="admin-profile-download-btn">
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminProfile;